import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-service-orbit-visual',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './service-orbit-visual.component.html',
  styleUrl: './service-orbit-visual.component.scss',
})
export class ServiceOrbitVisualComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) iconClass = '';

  @ViewChild('visualRoot', { static: true }) private visualRoot?: ElementRef<HTMLElement>;

  private gsapContext?: { revert: () => void };

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object) {}

  ngAfterViewInit(): void {
    this.setupAnimation();
  }

  ngOnDestroy(): void {
    this.gsapContext?.revert();
  }

  private async setupAnimation(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || !this.visualRoot?.nativeElement) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const { gsap } = await import('gsap');
    const root = this.visualRoot.nativeElement;

    this.gsapContext = gsap.context(() => {
      gsap.fromTo('.service-orbit-core', {
        autoAlpha: 0,
        y: 22,
        scale: 0.86,
      }, {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        duration: 1,
        ease: 'expo.out',
      });

      gsap.fromTo('.service-orbit-ring', {
        autoAlpha: 0,
        scale: 0.82,
      }, {
        autoAlpha: 1,
        scale: 1,
        duration: 1.15,
        ease: 'expo.out',
        stagger: 0.08,
      });

      gsap.to('.service-orbit-ring--outer, .service-orbit-ring--middle', {
        rotate: 360,
        transformOrigin: '50% 50%',
        duration: 34,
        ease: 'none',
        repeat: -1,
      });

      gsap.to('.service-orbit-ring--dashed, .service-orbit-ring--inner', {
        rotate: -360,
        transformOrigin: '50% 50%',
        duration: 28,
        ease: 'none',
        repeat: -1,
      });

      gsap.to('.service-orbit-glow', {
        scale: 1.07,
        opacity: 0.7,
        duration: 2.6,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      });

      gsap.to('.service-orbit-core', {
        y: -7,
        duration: 2.4,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      });

      gsap.to('.service-orbit-particle', {
        scale: 1.55,
        opacity: 0.38,
        duration: 1.5,
        ease: 'sine.inOut',
        stagger: {
          each: 0.18,
          repeat: -1,
          yoyo: true,
        },
      });

      gsap.fromTo('.service-orbit-ground', {
        autoAlpha: 0,
        scaleX: 0.58,
      }, {
        autoAlpha: 1,
        scaleX: 1,
        duration: 1,
        ease: 'expo.out',
        delay: 0.16,
      });
    }, root);
  }
}
